import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Linkedin, Instagram, Facebook } from "lucide-react";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { useLanguage } from "../i18n";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/servizi", label: "Servizi" },
  { to: "/siti-web", label: "Siti web" },
  { to: "/consulenze", label: "Consulenze" },
  { to: "/chi-siamo", label: "Chi siamo" },
  { to: "/blog", label: "Blog" },
  { to: "/contatti", label: "Contatti" },
];

const SERVICE_LINKS = [
  { to: "/siti-web", label: "Realizzazione siti web" },
  { to: "/servizi", label: "Redesign siti web" },
  { to: "/consulenze", label: "Presenza digitale" },
  { to: "/recensioni-nfc", label: "Recensioni NFC" },
];

export const Footer = () => {
  const [settings, setSettings] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const socials = [
    { url: settings?.linkedin, Icon: Linkedin, label: "LinkedIn" },
    { url: settings?.instagram, Icon: Instagram, label: "Instagram" },
    { url: settings?.facebook, Icon: Facebook, label: "Facebook" },
  ].filter((s) => s.url);

  return (
    <footer className="noise-overlay bg-petrolioInk text-avorio" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-4">
          <Logo dark />
          <p className="max-w-xs text-sm leading-relaxed text-avorio/70">
            {t("Siti web moderni e soluzioni digitali pratiche per piccole imprese e attività indipendenti in Svizzera.")}
          </p>
          {socials.length > 0 && (
            <div className="flex gap-3 pt-1">
              {socials.map(({ url, Icon, label }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="rounded-full border border-avorio/20 p-2.5 text-avorio/80 transition-colors hover:border-salvia hover:text-salvia"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-salvia">{t("Esplora")}</h3>
          <ul className="space-y-2.5">
            {QUICK_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-avorio/70 transition-colors hover:text-avorio">
                  {t(l.label)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-salvia">{t("Servizi")}</h3>
          <ul className="space-y-2.5">
            {SERVICE_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-avorio/70 transition-colors hover:text-avorio">
                  {t(l.label)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-salvia">{t("Contatti")}</h3>
          <ul className="space-y-2.5 text-sm text-avorio/70">
            {settings?.contact_email && (
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-salvia" />
                <a href={`mailto:${settings.contact_email}`} className="transition-colors hover:text-avorio" data-testid="footer-email">
                  {settings.contact_email}
                </a>
              </li>
            )}
            {settings?.phone && (
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-salvia" />
                <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-avorio" data-testid="footer-phone">
                  {settings.phone}
                </a>
              </li>
            )}
            {settings?.response_time && <li>{t("Tempi di risposta: ")}{settings.response_time}</li>}
            <li>
              <Link to="/contatti" className="text-salvia underline-offset-4 transition-colors hover:text-avorio hover:underline">
                {t("Scrivici dal modulo di contatto")}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-avorio/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-6 text-xs text-avorio/50 sm:px-8 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Alsera Studio. {t("Tutti i diritti riservati.")}</p>
          <p data-testid="footer-legal">{settings?.legal_info || t("Dati legali e fiscali configurabili dal pannello di amministrazione.")}</p>
          <div className="flex gap-5">
            <Link to="/privacy" className="transition-colors hover:text-avorio" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" className="transition-colors hover:text-avorio" data-testid="footer-cookie-link">
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
